/*
  assets/gcitypartners-check-engine.js — GCITYPARTNERS-CHECK-ENGINE-03
  Canonical owner: deterministic local readiness checks inside the existing GCityPartners review brief.

  Contract:
  - reads only explicit data-check-* fields from the active check definition;
  - calculates a deterministic readiness score (yes=1, partial=.5, unknown/no/unanswered=0, n/a excluded);
  - renders an informal readiness summary into existing Liquid-owned hooks;
  - persists only whitelisted deterministic check answers/result metadata in sessionStorage when available;
  - carries only opaque record id + non-sensitive current check metadata to the existing Contact handoff;
  - optionally explains an already-calculated result through a same-origin server route after explicit user action;
  - AI explanation receives only check type, fixed score/status and field keys; it cannot change scoring or become a professional conclusion;
  - no raw project location, contact data, free-text brief, backend check submission, checkout or file upload.
*/
(function (root, factory) {
  'use strict';

  if (root && root.GCityPartnersCheckEngine && root.GCityPartnersCheckEngine.version === '1.3.0-brief-owner-04') {
    root.GCityPartnersCheckEngine.bind(root.document, root);
    return;
  }

  var api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.GCityPartnersCheckEngine = Object.freeze(api);

  if (root && root.document) api.bind(root.document, root);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var VERSION = '1.3.0-brief-owner-04';
  var SCHEMA = 'gcitypartners_check_result_v1';
  var STORAGE_PREFIX = 'gcitypartners_check_result_v1:';
  var EXPLAIN_ENDPOINT = '/apps/gcity-api/content/check/explain';
  var EXPLAIN_REQUEST_CONTRACT = 'gcitypartners_check_explain_request_v1';
  var EXPLAIN_RESPONSE_CONTRACT = 'gcitypartners_check_explanation_v1';
  var ROOT_SELECTOR = '[data-gcp-contextual-brief]';
  var FORM_SELECTOR = '[data-enterprise-triage-form]';
  var NEED_SELECTOR = '[name="business_need"]';
  var DEFINITION_SELECTOR = '[data-check-definition]';
  var FIELD_SELECTOR = '[data-check-field]';
  var REPORT_SELECTOR = '[data-gcity-check-report]';
  var CHECK_TYPES = [
    'school-certificate-check',
    'land-approval-readiness',
    'land-risk-check',
    'industrial-community-check',
    'contract-extension-check'
  ];
  var CHECK_STATUSES = ['insufficient', 'prepare', 'review-ready', 'well-prepared'];
  var SAFE_ANSWERS = ['yes', 'partial', 'unknown', 'no', 'na'];
  var ANSWER_FACTOR = Object.freeze({ yes: 1, partial: 0.5, unknown: 0, no: 0, na: null });
  var mountedRoots = typeof WeakSet === 'function' ? new WeakSet() : null;
  var boundDocuments = typeof WeakSet === 'function' ? new WeakSet() : null;
  var latestResults = typeof WeakMap === 'function' ? new WeakMap() : null;

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function safeType(value) {
    var type = clean(value);
    return CHECK_TYPES.indexOf(type) >= 0 ? type : '';
  }

  function safeStatus(value) {
    var status = clean(value);
    return CHECK_STATUSES.indexOf(status) >= 0 ? status : '';
  }

  function safeAnswer(value) {
    var answer = clean(value).toLowerCase();
    return SAFE_ANSWERS.indexOf(answer) >= 0 ? answer : '';
  }

  function safeFieldKey(value) {
    var key = clean(value).toLowerCase();
    return /^[a-z0-9][a-z0-9_-]{0,79}$/.test(key) ? key : '';
  }

  function safeLocale(value) {
    var locale = clean(value).toLowerCase();
    if (locale === 'zh-cn' || locale === 'zh-hans' || locale === 'zh-sg') return 'zh-CN';
    if (locale === 'zh-hk' || locale === 'zh-hant') return 'zh-HK';
    if (locale === 'zh-tw') return 'zh-TW';
    return 'en';
  }

  function positiveWeight(value) {
    var weight = Number(value);
    return Number.isFinite(weight) && weight > 0 ? weight : 0;
  }

  function classify(score) {
    var value = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
    if (value >= 90) return 'well-prepared';
    if (value >= 70) return 'review-ready';
    if (value >= 40) return 'prepare';
    return 'insufficient';
  }

  function calculate(fields, answers) {
    var available = 0;
    var earned = 0;
    var completed = [];
    var partial = [];
    var missing = [];
    var actions = [];

    (Array.isArray(fields) ? fields : []).forEach(function (field) {
      var key = clean(field && field.key);
      var weight = positiveWeight(field && field.weight);
      if (!key || !weight) return;

      var supplied = safeAnswer(answers && answers[key]);
      var answer = supplied || 'unknown';
      var factor = ANSWER_FACTOR[answer];
      if (factor === null) return;

      available += weight;
      earned += weight * factor;

      var item = {
        key: key,
        label: clean(field.label) || key,
        answer: answer
      };

      if (answer === 'yes') completed.push(item);
      else if (answer === 'partial') partial.push(item);
      else missing.push(item);

      if (answer !== 'yes' && clean(field.action)) actions.push(clean(field.action));
    });

    var score = available > 0 ? Math.round((earned / available) * 100) : 0;
    return {
      score: score,
      status: classify(score),
      completed: completed,
      partial: partial,
      missing: missing,
      actions: Array.from(new Set(actions))
    };
  }

  function allowedKeys(fields) {
    return new Set((Array.isArray(fields) ? fields : []).map(function (field) {
      return clean(field && field.key);
    }).filter(Boolean));
  }

  function buildResult(input) {
    var source = input && typeof input === 'object' ? input : {};
    var fields = Array.isArray(source.fields) ? source.fields : [];
    var type = safeType(source.type);
    var briefId = opaque(source.brief_id);
    var answers = source.answers && typeof source.answers === 'object' ? source.answers : {};
    var keys = allowedKeys(fields);
    var calculated = calculate(fields, answers);

    return Object.assign({
      schema: SCHEMA,
      version: VERSION,
      type: type,
      brief_id: briefId,
      scope: 'informal-readiness-check',
      answers: Object.keys(answers).reduce(function (accumulator, key) {
        var safeKey = clean(key);
        var answer = safeAnswer(answers[key]);
        if (keys.has(safeKey) && answer) accumulator[safeKey] = answer;
        return accumulator;
      }, {}),
      handoff: {
        recommended: calculated.score < 90,
        type: 'professional-review'
      }
    }, calculated);
  }

  function resultKeys(items) {
    return Array.from(new Set((Array.isArray(items) ? items : []).map(function (item) {
      return safeFieldKey(item && item.key);
    }).filter(Boolean))).slice(0, 20);
  }

  function buildExplainRequest(result, locale) {
    var source = result && typeof result === 'object' ? result : {};
    var type = safeType(source.type);
    var status = safeStatus(source.status);
    var numericScore = Number(source.score);
    var score = Number.isFinite(numericScore) ? Math.max(0, Math.min(100, Math.round(numericScore))) : 0;
    return {
      contract: EXPLAIN_REQUEST_CONTRACT,
      check_type: type,
      score: score,
      status: status || classify(score),
      completed: resultKeys(source.completed),
      partial: resultKeys(source.partial),
      missing: resultKeys(source.missing),
      locale: safeLocale(locale)
    };
  }

  function fieldLabel(node) {
    var labelNode = node && node.closest ? node.closest('label') : null;
    var explicit = labelNode && labelNode.querySelector('[data-check-field-label]');
    return clean(explicit ? explicit.textContent : (node && node.getAttribute('data-check-label')));
  }

  function definitionFields(definition) {
    if (!definition || !definition.querySelectorAll) return [];
    return Array.prototype.map.call(definition.querySelectorAll(FIELD_SELECTOR), function (node) {
      return {
        key: clean(node.getAttribute('data-check-field')),
        weight: positiveWeight(node.getAttribute('data-check-weight')),
        label: fieldLabel(node),
        action: clean(node.getAttribute('data-check-action'))
      };
    }).filter(function (field) { return Boolean(field.key && field.weight); });
  }

  function definitionAnswers(definition) {
    var answers = {};
    if (!definition || !definition.querySelectorAll) return answers;
    Array.prototype.forEach.call(definition.querySelectorAll(FIELD_SELECTOR), function (node) {
      var key = clean(node.getAttribute('data-check-field'));
      var answer = safeAnswer(node.value);
      if (key && answer) answers[key] = answer;
    });
    return answers;
  }

  function selectedDefinition(form) {
    var need = form && form.querySelector(NEED_SELECTOR);
    var type = safeType(need ? need.value : '');
    if (!type || !form.querySelectorAll) return null;
    var definitions = Array.prototype.slice.call(form.querySelectorAll(DEFINITION_SELECTOR));
    return definitions.find(function (definition) {
      return safeType(definition.getAttribute('data-check-definition')) === type;
    }) || null;
  }

  function recordId(form) {
    var node = form && form.elements && form.elements.namedItem('record_id');
    return opaque(node && node.value);
  }

  function storageKey(briefId) {
    var id = opaque(briefId);
    return id ? STORAGE_PREFIX + id : '';
  }

  function sessionStorageOf(win) {
    if (!win) return null;
    try {
      return win.sessionStorage || null;
    } catch (_error) {
      return null;
    }
  }

  function storageAvailable(win) {
    return Boolean(sessionStorageOf(win));
  }

  function remember(win, result) {
    var key = storageKey(result && result.brief_id);
    var storage = sessionStorageOf(win);
    if (!key || !storage) return false;
    var payload = {
      schema: SCHEMA,
      version: VERSION,
      type: safeType(result.type),
      brief_id: opaque(result.brief_id),
      answers: result.answers,
      score: result.score,
      status: result.status
    };
    try {
      storage.setItem(key, JSON.stringify(payload));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function recall(win, briefId) {
    var key = storageKey(briefId);
    var storage = sessionStorageOf(win);
    if (!key || !storage) return null;
    try {
      var parsed = JSON.parse(storage.getItem(key) || 'null');
      if (!parsed || parsed.schema !== SCHEMA || opaque(parsed.brief_id) !== opaque(briefId)) return null;
      if (!safeType(parsed.type)) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  function writeText(report, selector, value) {
    var node = report && report.querySelector(selector);
    if (node) node.textContent = value;
  }

  function joinLabels(items) {
    var labels = (Array.isArray(items) ? items : []).map(function (item) {
      return clean(item && item.label);
    }).filter(Boolean);
    return labels.length ? labels.join(' · ') : '—';
  }

  function statusLabel(report, status) {
    if (!report) return status;
    return clean(report.getAttribute('data-status-' + status)) || status;
  }

  function documentLanguage(form) {
    return clean(form && form.ownerDocument && form.ownerDocument.documentElement.lang).toLowerCase();
  }

  function localizedCopy(form) {
    var locale = safeLocale(documentLanguage(form));
    if (locale === 'zh-CN') {
      return {
        button: '用 AI 详细说明评估结果', loading: '正在分析…', error: '暂时无法生成说明，请稍后再试。',
        title: 'AI 评估说明', ready: '已有资料', prepare: '待补充事项', next: '建议下一步', boundary: '注意事项'
      };
    }
    if (locale === 'zh-TW' || locale === 'zh-HK') {
      return {
        button: '用 AI 詳細說明評估結果', loading: '正在分析…', error: '暫時無法生成說明，請稍後再試。',
        title: 'AI 評估說明', ready: '已有資料', prepare: '待補充事項', next: '建議下一步', boundary: '注意事項'
      };
    }
    return {
      button: 'Explain result with AI', loading: 'Analyzing…', error: 'Explanation is temporarily unavailable. Please try again.',
      title: 'AI Insights', ready: 'Ready Items', prepare: 'Items to Prepare', next: 'Suggested Next Steps', boundary: 'Important Note'
    };
  }

  function clearExplanation(form) {
    var report = form && form.querySelector(REPORT_SELECTOR);
    if (!report) return;
    var explanation = report.querySelector('[data-check-explanation]');
    var status = report.querySelector('[data-check-explain-status]');
    var button = report.querySelector('[data-check-explain]');
    if (explanation) {
      explanation.hidden = true;
      Array.prototype.forEach.call(explanation.querySelectorAll('[data-check-explanation-value]'), function (node) {
        node.textContent = '';
      });
    }
    if (status) status.textContent = '';
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function ensureExplanationControls(form, report, win) {
    if (!form || !report || !win || typeof win.fetch !== 'function') return null;
    var existing = report.querySelector('[data-check-explain-controls]');
    if (existing) return existing;

    var doc = report.ownerDocument;
    var copy = localizedCopy(form);
    var wrap = doc.createElement('div');
    wrap.setAttribute('data-check-explain-controls', '');
    wrap.setAttribute('data-ai-role', 'explanation-only');

    var button = doc.createElement('button');
    button.type = 'button';
    button.className = 'gcp-btn gcp-btn--secondary';
    button.setAttribute('data-check-explain', '');
    button.textContent = copy.button;

    var status = doc.createElement('p');
    status.setAttribute('data-check-explain-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    var explanation = doc.createElement('section');
    explanation.setAttribute('data-check-explanation', '');
    explanation.setAttribute('aria-live', 'polite');
    explanation.hidden = true;

    function addLine(label, hook) {
      var p = doc.createElement('p');
      var strong = doc.createElement('strong');
      strong.textContent = label;
      var span = doc.createElement('span');
      span.setAttribute(hook, '');
      span.setAttribute('data-check-explanation-value', '');
      p.appendChild(strong);
      p.appendChild(doc.createElement('br'));
      p.appendChild(span);
      explanation.appendChild(p);
    }

    addLine(copy.title, 'data-check-explanation-summary');
    addLine(copy.ready, 'data-check-explanation-ready');
    addLine(copy.prepare, 'data-check-explanation-prepare');
    addLine(copy.next, 'data-check-explanation-next');
    addLine(copy.boundary, 'data-check-explanation-boundary');

    wrap.appendChild(button);
    wrap.appendChild(status);
    wrap.appendChild(explanation);
    report.appendChild(wrap);

    button.addEventListener('click', function () {
      var result = latestResults && latestResults.get(form);
      if (!result) return;
      requestExplanation(form, result, win);
    });

    return wrap;
  }

  function unwrapPayload(payload) {
    if (!payload || typeof payload !== 'object') return {};
    if (payload.contract === EXPLAIN_RESPONSE_CONTRACT) return payload;
    if (payload.data && payload.data.contract === EXPLAIN_RESPONSE_CONTRACT) return payload.data;
    if (payload.data && payload.data.data && payload.data.data.contract === EXPLAIN_RESPONSE_CONTRACT) return payload.data.data;
    return payload;
  }

  function safeExplanation(value) {
    var source = value && typeof value === 'object' ? value : {};
    function safeText(input) { return clean(input).slice(0, 700); }
    function safeList(input) {
      return (Array.isArray(input) ? input : []).map(safeText).filter(Boolean).slice(0, 8);
    }
    return {
      summary: safeText(source.summary),
      what_is_ready: safeList(source.what_is_ready),
      what_to_prepare: safeList(source.what_to_prepare),
      next_steps: safeList(source.next_steps),
      boundary_note: safeText(source.boundary_note)
    };
  }

  function renderExplanation(form, payload) {
    var report = form && form.querySelector(REPORT_SELECTOR);
    var panel = report && report.querySelector('[data-check-explanation]');
    if (!panel) return;
    var explanation = safeExplanation(payload && payload.explanation);
    writeText(panel, '[data-check-explanation-summary]', explanation.summary || '—');
    writeText(panel, '[data-check-explanation-ready]', explanation.what_is_ready.join(' · ') || '—');
    writeText(panel, '[data-check-explanation-prepare]', explanation.what_to_prepare.join(' · ') || '—');
    writeText(panel, '[data-check-explanation-next]', explanation.next_steps.join(' → ') || '—');
    writeText(panel, '[data-check-explanation-boundary]', explanation.boundary_note || '—');
    panel.hidden = false;
  }

  async function requestExplanation(form, result, win) {
    var report = form && form.querySelector(REPORT_SELECTOR);
    if (!report || !win || typeof win.fetch !== 'function') return null;
    var button = report.querySelector('[data-check-explain]');
    var status = report.querySelector('[data-check-explain-status]');
    var copy = localizedCopy(form);
    var payload = buildExplainRequest(result, documentLanguage(form));
    if (!payload.check_type || !payload.status) return null;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (status) status.textContent = copy.loading;

    try {
      var response = await win.fetch(EXPLAIN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      var body = await response.json().catch(function () { return {}; });
      var data = unwrapPayload(body);
      if (!response.ok || data.contract !== EXPLAIN_RESPONSE_CONTRACT || !data.explanation) throw new Error('check_explanation_unavailable');
      renderExplanation(form, data);
      if (status) status.textContent = '';
      return data;
    } catch (_error) {
      if (status) status.textContent = copy.error;
      return null;
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  function render(form, result, win) {
    var report = form && form.querySelector(REPORT_SELECTOR);
    if (!report || !result || !safeType(result.type)) return;

    writeText(report, '[data-check-score]', String(result.score));
    writeText(report, '[data-check-status]', statusLabel(report, result.status));
    writeText(report, '[data-check-completed-count]', String(result.completed.length));
    writeText(report, '[data-check-missing-count]', String(result.partial.length + result.missing.length));
    writeText(report, '[data-check-completed]', joinLabels(result.completed));
    writeText(report, '[data-check-partial]', joinLabels(result.partial));
    writeText(report, '[data-check-missing]', joinLabels(result.missing));
    writeText(report, '[data-check-actions]', result.actions.length ? result.actions.join(' → ') : '—');

    report.hidden = false;
    report.setAttribute('data-check-type', result.type);
    report.setAttribute('data-check-state', result.status);
    report.setAttribute('data-check-score-value', String(result.score));
    report.setAttribute('data-check-contract', SCHEMA);
    report.setAttribute('data-check-ai-explanation', 'optional');
    if (latestResults) latestResults.set(form, result);
    ensureExplanationControls(form, report, win);
    clearExplanation(form);
  }

  function clearHandoff(form) {
    var link = form && form.querySelector('[data-gcp-safe-contact-handoff]');
    if (!link || !link.href) return;
    try {
      var url = new URL(link.href, link.ownerDocument.baseURI);
      url.searchParams.delete('check_type');
      url.searchParams.delete('check_status');
      url.searchParams.delete('check_score');
      link.href = url.pathname + url.search + url.hash;
      link.removeAttribute('data-check-handoff');
    } catch (_error) {}
  }

  function hideReport(form) {
    var report = form && form.querySelector(REPORT_SELECTOR);
    if (report) report.hidden = true;
    if (latestResults) latestResults.delete(form);
    clearExplanation(form);
    clearHandoff(form);
  }

  function decorateHandoff(form, result) {
    var link = form && form.querySelector('[data-gcp-safe-contact-handoff]');
    if (!link || !link.href || !result || !safeType(result.type)) return;
    try {
      var url = new URL(link.href, link.ownerDocument.baseURI);
      url.searchParams.set('check_type', result.type);
      url.searchParams.set('check_status', result.status);
      url.searchParams.set('check_score', String(result.score));
      link.href = url.pathname + url.search + url.hash;
      link.setAttribute('data-check-handoff', result.type);
    } catch (_error) {}
  }

  function resultForForm(form) {
    var definition = selectedDefinition(form);
    if (!definition) return null;
    return buildResult({
      type: safeType(definition.getAttribute('data-check-definition')),
      brief_id: recordId(form),
      fields: definitionFields(definition),
      answers: definitionAnswers(definition)
    });
  }

  function updateSubmitLabel(form, isCheck, restored) {
    var button = form && form.querySelector('[data-enterprise-triage-submit]');
    if (!button || !isCheck) return;
    var locale = safeLocale(documentLanguage(form));
    var labels = {
      'zh-CN': {
        checkRestored: '更新资料并重新评估',
        checkNew: '保存资料并查看评估结果'
      },
      'zh-HK': {
        checkRestored: '更新資料並重新評估',
        checkNew: '儲存資料並查看評估結果'
      },
      'zh-TW': {
        checkRestored: '更新資料並重新評估',
        checkNew: '儲存資料並查看評估結果'
      },
      'en': {
        checkRestored: 'Update details and re-evaluate',
        checkNew: 'Save details and view evaluation'
      }
    };
    var current = labels[locale] || labels['en'];
    button.textContent = restored ? current.checkRestored : current.checkNew;
  }

  function isImportedUavBrief(form) {
    var precheck = form && form.querySelector('[name="precheck_type"]');
    return clean(precheck && precheck.value) === 'uav-3d';
  }

  function syncDefinition(form, win) {
    if (!form) return;
    if (isImportedUavBrief(form)) {
      Array.prototype.forEach.call(form.querySelectorAll(DEFINITION_SELECTOR), function (definition) {
        definition.hidden = true;
        Array.prototype.forEach.call(definition.querySelectorAll(FIELD_SELECTOR), function (node) {
          node.disabled = true;
          node.required = false;
        });
      });
      hideReport(form);
      return;
    }
    var active = selectedDefinition(form);
    var isCheck = Boolean(active);

    Array.prototype.forEach.call(form.querySelectorAll(DEFINITION_SELECTOR), function (definition) {
      var enabled = definition === active;
      definition.hidden = !enabled;
      Array.prototype.forEach.call(definition.querySelectorAll(FIELD_SELECTOR), function (node) {
        node.disabled = !enabled;
        node.required = enabled;
      });
    });

    Array.prototype.forEach.call(form.querySelectorAll('[data-check-optional-context]'), function (container) {
      container.hidden = isCheck;
      Array.prototype.forEach.call(container.querySelectorAll('input,select,textarea'), function (node) {
        node.disabled = isCheck;
        if (isCheck) node.removeAttribute('required');
        else if (node.getAttribute('data-check-required') === 'true') node.required = true;
      });
    });

    if (!isCheck) {
      hideReport(form);
      return;
    }

    hideReport(form);
    var saved = recall(win, recordId(form));
    if (saved && saved.type === safeType(active.getAttribute('data-check-definition'))) {
      Array.prototype.forEach.call(active.querySelectorAll(FIELD_SELECTOR), function (node) {
        var key = clean(node.getAttribute('data-check-field'));
        var answer = safeAnswer(saved.answers && saved.answers[key]);
        if (answer) node.value = answer;
      });
      var restored = resultForForm(form);
      if (restored) {
        render(form, restored, win);
        decorateHandoff(form, restored);
      }
      updateSubmitLabel(form, true, true);
    } else {
      updateSubmitLabel(form, true, false);
    }
  }

  function bindRoot(rootNode, win) {
    if (!rootNode || (mountedRoots && mountedRoots.has(rootNode))) return;
    var form = rootNode.querySelector(FORM_SELECTOR);
    if (!form) return;
    if (mountedRoots) mountedRoots.add(rootNode);

    var need = form.querySelector(NEED_SELECTOR);
    if (need) need.addEventListener('change', function () { syncDefinition(form, win); });

    Array.prototype.forEach.call(form.querySelectorAll(FIELD_SELECTOR), function (field) {
      field.addEventListener('change', function () { hideReport(form); });
    });

    syncDefinition(form, win);
  }

  function bind(documentNode, win) {
    var doc = documentNode;
    var browser = win || (typeof window !== 'undefined' ? window : null);
    if (!doc || !doc.querySelectorAll || !browser) return;

    Array.prototype.forEach.call(doc.querySelectorAll(ROOT_SELECTOR), function (rootNode) {
      bindRoot(rootNode, browser);
    });

    if (boundDocuments && boundDocuments.has(doc)) return;
    if (boundDocuments) boundDocuments.add(doc);

    browser.addEventListener('gcitypartners:review-brief:saved', function () {
      Array.prototype.forEach.call(doc.querySelectorAll(ROOT_SELECTOR), function (rootNode) {
        var form = rootNode.querySelector(FORM_SELECTOR);
        if (isImportedUavBrief(form)) return;
        var result = resultForForm(form);
        if (!result || !result.brief_id || !result.type) return;
        remember(browser, result);
        render(form, result, browser);
        decorateHandoff(form, result);
        updateSubmitLabel(form, true, true);
      });
    });

    doc.addEventListener('shopify:section:load', function (event) {
      var scope = event.target || doc;
      if (scope.matches && scope.matches(ROOT_SELECTOR)) bindRoot(scope, browser);
      if (scope.querySelectorAll) {
        Array.prototype.forEach.call(scope.querySelectorAll(ROOT_SELECTOR), function (rootNode) {
          bindRoot(rootNode, browser);
        });
      }
    });
  }

  return {
    version: VERSION,
    schema: SCHEMA,
    storagePrefix: STORAGE_PREFIX,
    explainEndpoint: EXPLAIN_ENDPOINT,
    explainRequestContract: EXPLAIN_REQUEST_CONTRACT,
    explainResponseContract: EXPLAIN_RESPONSE_CONTRACT,
    checkTypes: CHECK_TYPES.slice(),
    safeAnswers: SAFE_ANSWERS.slice(),
    classify: classify,
    calculate: calculate,
    buildResult: buildResult,
    buildExplainRequest: buildExplainRequest,
    storageAvailable: storageAvailable,
    bind: bind,
    boundary: Object.freeze({
      deterministic_scoring_only: true,
      session_only: true,
      storage_optional: true,
      no_contact_persistence: true,
      no_raw_location_persistence: true,
      no_check_backend_submission: true,
      ai_explanation_optional: true,
      ai_can_change_score: false,
      ai_explanation_persisted: false,
      no_raw_location_to_ai: true,
      no_contact_to_ai: true,
      no_professional_conclusion: true,
      no_checkout: true,
      unanswered_counts_as_missing: true,
      na_excluded_from_denominator: true,
      stale_handoff_metadata_cleared: true
    })
  };
});
